<ng-container
  *ngIf="{
    isEditActive: isEditActive$ | async,
    pendingAllocations: pendingAllocations$ | async,
    existingAllocations: existingAllocations$ | async,
    allocationStatus: allocationStatus$ | async,
    allocationStatuses: allocationStatuses$ | async,
    amountsRemaining: amountsRemaining$ | async,
  } as globalData"
>
  <div class="bg-color-gray-1100 rounded-all-1 mb-3" [ngClass]="{ 'pb-0': globalData.isEditActive }">
    <div class="d-flex align-items-center justify-content-between py-2 px-4">
      <div class="d-flex align-items-center">
        <mat-checkbox
          *ngIf="globalData.isEditActive && headerCheckboxState$ | async as headerCheckboxState"
          #headerCheckbox
          class="me-4"
          [attr.aria-label]="headerCheckboxState === CheckboxState.CHECKED ? 'Fully auto-allocate' : 'Clear allocations'"
          [checked]="headerCheckboxState === CheckboxState.CHECKED"
          [disabled]="headerCheckboxState === CheckboxState.DISABLED"
          [id]="selectedArTran$.value.data?.id.toString()"
          [indeterminate]="headerCheckboxState === CheckboxState.INDETERMINATE"
          (change)="onHeaderCheckboxClicked(headerCheckboxState)"
          (keydown.enter)="onHeaderCheckboxClicked(headerCheckboxState)"
        />

        <div class="d-flex flex-column">
          <p class="color-gray-100 body-400">This Transaction</p>
          <p
            *ngIf="selectedArTran$.value.data as selectedArTran"
            class="small-300 mt-1"
            [ngClass]="{
              'color-gray-400': selectedArTran.amountRemaining === 0,
              'color-gray-100': selectedArTran.amountRemaining > 0,
            }"
          >
            {{ selectedArTran.amountRemaining | extendedCurrency }} remaining
          </p>
        </div>
      </div>

      <div class="d-flex flex-column gap-1 align-items-end">
        <div class="d-flex align-items-center gap-4">
          <anduin-icon
            *ngIf="allocationStatusIconDetails$ | async as icon"
            [ngClass]="icon.class"
            [name]="icon.name"
            [stroke]="icon.color"
          />

          <p class="small-300 color-gray-400">Allocated amount</p>
        </div>

        <p
          *ngIf="{ value: allocationTotal$ | async } as allocationTotal"
          class="body-400 color-gray-100"
          [ngClass]="{ 'color-red-300': allocationsForm?.errors?.overAllocated }"
        >
          {{ allocationTotal.value | extendedCurrency }}
        </p>
      </div>
    </div>

    <ng-content select="[actions]" />
  </div>

  <div
    #allocationsContainer
    [tabIndex]="globalData.isEditActive && allTransactionIds?.length > 0 ? 0 : -1"
    class="transaction-allocations-container"
    [attr.aria-label]="
      'Allocations container. Use navigation keys to move between transactions. Space toggles full auto-allocation.' +
      'Escape focuses on the discard button. Enter focuses on the save button.'
    "
    (keydown)="globalData.isEditActive && onAllocationsContainerKeyDown($event)"
  >
    <ng-container
      *ngFor="let linkedTransaction of (linkedArTrans$ | async).data"
      [ngTemplateOutlet]="transactionRowTemplate"
      [ngTemplateOutletContext]="{ $implicit: linkedTransaction }"
    />

    <ng-container *ngIf="globalData.isEditActive">
      <ng-container
        *ngFor="let linkableTransaction of (linkableArTrans$ | async).data"
        [ngTemplateOutlet]="transactionRowTemplate"
        [ngTemplateOutletContext]="{ $implicit: linkableTransaction }"
      />
    </ng-container>

    <div *ngIf="shouldShowLoadingState$ | async" class="w-100 center-content py-2 px-4">
      <mat-spinner diameter="44" />
    </div>

    <div *ngIf="shouldShowErrorState$ | async" class="alert alert--error">Failed to load allocatable transactions</div>

    <div *ngIf="shouldShowZeroState$ | async" class="alert alert--info">
      {{ zeroStateMessage$ | async }}
    </div>
  </div>

  <ng-template #transactionRowTemplate let-transaction>
    <div
      *ngIf="{
        formControl: allocationsForm?.get(transaction.id.toString()),
        existingAllocation: globalData.existingAllocations[transaction.id],
        pendingAllocation: globalData.pendingAllocations[transaction.id],
        allocationStatus: globalData.allocationStatuses[transaction.id],
        amountRemaining: globalData.amountsRemaining[transaction.id],
      } as rowData"
      tabindex="-1"
      class="transaction-allocation-row d-flex align-items-center justify-content-between py-2"
      [ngClass]="{ 'should-show-focus-state': globalData.isEditActive }"
      [attr.aria-label]="
        transaction.typeHeader +
        ' ' +
        (transaction.reference ?? transaction.invoiceNumber ?? '') +
        '. ' +
        rowData.amountRemaining +
        ' remaining, ' +
        (rowData.pendingAllocation || 'nothing') +
        ' allocated. ' +
        'Transaction row. Press space or enter to toggle allocation. ' +
        'Start typing a numeric value to edit allocation amount. Press escape to focus on allocations container.'
      "
      [id]="transaction.id"
      (keydown)="globalData.isEditActive && onAllocationsRowKeyDown($event, transaction)"
    >
      <!-- left -->
      <mat-checkbox
        *ngIf="globalData.isEditActive"
        class="me-4"
        [attr.aria-label]="
          (rowData.pendingAllocation ? 'Deallocate' : 'Allocate') +
          ' ' +
          transaction.typeHeader +
          ' ' +
          (transaction.reference ?? transaction.invoiceNumber ?? '') +
          '. ' +
          'Use arrow up or down keys to navigate between transactions. Press escape to return focus to the current row.'
        "
        [checked]="!!rowData.pendingAllocation"
        [disabled]="
          !selectedArTran$.value.data?.amount ||
          (!rowData.pendingAllocation && (globalData.allocationStatus === AllocationStatus.FULL || allocationsForm.errors?.overAllocated))
        "
        [id]="transaction.id.toString()"
        (keydown)="onTransactionCheckboxKeyDown($event, transaction)"
        (change)="onBodyCheckboxClicked(!!rowData.pendingAllocation, transaction)"
      />

      <!-- middle -->
      <div class="d-flex flex-column gap-1 text-break me-1">
        <div class="d-flex align-items-baseline gap-1 flex-wrap">
          <p class="color-gray-100 body-400">
            {{ transaction.typeHeader || (transaction.type === 'LateFee' ? 'Late Fee' : transaction.type) }}
          </p>
          <p class="color-gray-400 small-300">
            {{ transaction.reference ?? transaction.invoiceNumber ?? (transaction.type === 'LateFee' ? transaction.id : '') }}
          </p>
        </div>

        <div class="d-flex gap-1 flex-wrap">
          <p
            class="body-400"
            [ngClass]="{
              'color-gray-400':
                !globalData.isEditActive || !(rowData.allocationStatus === AllocationStatus.PARTIAL || rowData.amountRemaining < 0),
              'color-gold-100': globalData.isEditActive && rowData.allocationStatus === AllocationStatus.PARTIAL,
              'color-red-300': globalData.isEditActive && rowData.amountRemaining < 0,
            }"
          >
            {{ rowData.amountRemaining | extendedCurrency }}
          </p>
          <p class="color-gray-400 body-300">remaining</p>
        </div>
      </div>

      <!-- right -->
      <div
        *ngIf="globalData.isEditActive && rowData.formControl; else readonlyAllocationTemplate"
        class="d-flex flex-column align-items-end flex-1"
      >
        <input
          class="allocation-input form-input form-input--currency pe-3"
          [ngClass]="{
            'form-input--invalid':
              (rowData.formControl?.errors && canShowValidationErrors(rowData.formControl)) ||
              (rowData.formControl?.enabled && allocationsForm.errors),
          }"
          [attr.aria-label]="
            'Allocation amount input for ' +
            transaction.typeBody +
            ' ' +
            (transaction.reference ?? transaction.invoiceNumber ?? '') +
            '. ' +
            'Current allocation is ' +
            rowData.pendingAllocation +
            '. ' +
            'Press enter or escape to return focus to the current row.'
          "
          [formControl]="rowData.formControl"
          [maxDecimalPlaces]="2"
          (blur)="onAllocationInputBlurred($event.target.value, rowData.formControl)"
          (keydown)="onTransactionAllocationInputKeyDown($event, transaction)"
          exceptions="."
          numberOnly
          placeholder="0.00"
        />
        <mat-error *ngIf="rowData.formControl.errors?.max && canShowValidationErrors(rowData.formControl)" class="mt-1 small-300 ta--right">
          Allocation cannot be greater than amount remaining
        </mat-error>
      </div>

      <ng-template #readonlyAllocationTemplate>
        <p class="body-400 color-gray-100 pe-3">{{ rowData.existingAllocation | currency }}</p>
      </ng-template>
    </div>
  </ng-template>
</ng-container>
